// 設定 → AIアシスタント → AIプロバイダー（管理者専用カード）
// すべてのAIチャットボット/OCRをカスタム（ローカルLLM）へ切り替え・復帰できる。
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardDrives, ArrowCounterClockwise, PlugsConnected, Eye, ArrowsClockwise, PencilSimple, CaretUpDown, CloudSlash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai`;
const CONSOLE_API = `${process.env.REACT_APP_BACKEND_URL}/api/ai/console`;

const MANUAL = '__manual__';

function ModelSelect({ value, onChange, options, placeholder, testid }) {
  const [manual, setManual] = useState(false);
  const inList = options.some((m) => m.id === value);
  if (manual || (options.length === 0)) {
    return (
      <div className="flex items-center gap-1.5">
        <Input value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className="h-9 text-sm font-mono" data-testid={testid} />
        {options.length > 0 && (
          <button type="button" onClick={() => setManual(false)} title="一覧から選択"
            data-testid={`${testid}-to-select`}
            className="shrink-0 rounded-md border border-zinc-200 bg-white p-2 text-zinc-400 transition-colors hover:bg-zinc-50 hover:text-indigo-600">
            <CaretUpDown size={14} />
          </button>
        )}
      </div>
    );
  }
  return (
    <Select value={inList ? value : (value ? `__cur__${value}` : undefined)}
      onValueChange={(v) => {
        if (v === MANUAL) { setManual(true); return; }
        onChange(v.startsWith('__cur__') ? v.slice(7) : v);
      }}>
      <SelectTrigger className="h-9 text-sm font-mono bg-white" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {!inList && value && (
          <SelectItem value={`__cur__${value}`}>
            <span className="flex items-center gap-2 font-mono text-[13px]">{value}
              <span className="rounded border border-zinc-200 bg-zinc-50 px-1 py-px font-sans text-[9px] font-bold text-zinc-400">未検出</span>
            </span>
          </SelectItem>
        )}
        {options.map((m) => (
          <SelectItem key={m.id} value={m.id} data-testid={`${testid}-opt-${m.id}`}>
            <span className="flex items-center gap-2 font-mono text-[13px]">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${m.online === false ? 'bg-zinc-300' : m.state === 'loaded' ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
              {m.id}
              {m.vision && <span className="rounded border border-purple-200 bg-purple-50 px-1 py-px font-sans text-[9px] font-bold text-purple-700">VISION</span>}
            </span>
          </SelectItem>
        ))}
        <SelectItem value={MANUAL}>
          <span className="flex items-center gap-1.5 text-[12px] text-zinc-500"><PencilSimple size={12} /> 手動で入力…</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-zinc-400">{hint}</p>}
    </div>
  );
}

export default function AiProviderCard({ models: modelsProp, onModels }) {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState(null); // null = 変更なし
  const [cfSecret, setCfSecret] = useState(null); // null = 変更なし
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [modelsLocal, setModelsLocal] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const models = modelsProp ?? modelsLocal;
  const setModels = onModels ?? setModelsLocal;

  const load = () => axios.get(`${API}/provider`, { withCredentials: true })
    .then((r) => { setCfg(r.data); setApiKey(null); setCfSecret(null); }).catch(() => setCfg(null));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (modelsProp === undefined) {
      axios.get(`${CONSOLE_API}/models`, { withCredentials: true })
        .then((r) => setModelsLocal(r.data.models || [])).catch(() => {});
    }
  }, [modelsProp]);

  const detectModels = async () => {
    setDetecting(true);
    try {
      const r = await axios.get(`${CONSOLE_API}/models?refresh=1`, { withCredentials: true });
      setModels(r.data.models || []);
      toast.success(`${(r.data.models || []).filter((m) => m.online !== false).length} 件のモデルを検出しました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'モデル検出に失敗しました — エンドポイントURLと接続をご確認ください');
    } finally { setDetecting(false); }
  };

  const online = models.filter((m) => m.online !== false);
  const avail = online.length > 0 ? online : models;
  const textOptions = avail.filter((m) => !m.embedding);
  const visionOnly = avail.filter((m) => m.vision);
  const visionOptions = visionOnly.length > 0 ? visionOnly : textOptions;
  // L1 — 意味検索の埋め込みはローカルLLMの埋め込みモデルだけを使う
  const embeddingOnly = avail.filter((m) => m.embedding);
  const embeddingOptions = embeddingOnly.length > 0 ? embeddingOnly : avail;

  if (!cfg) return null;
  const isCustom = cfg.mode === 'custom';
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async (override = {}) => {
    setSaving(true);
    const body = {
      mode: cfg.mode, base_url: cfg.base_url,
      text_model: cfg.text_model, vision_model: cfg.vision_model,
      embedding_model: cfg.embedding_model || '',
      use_for_vision: cfg.use_for_vision,
      allow_emergent_calls: cfg.allow_emergent_calls, api_key: apiKey, ...override,
    };
    const put = async (version) => {
      const r = await axios.put(`${API}/provider`,
        { ...body, expected_version: version ?? null }, { withCredentials: true });
      setCfg(r.data); setApiKey(null); setTestResult(null);
      toast.success(body.mode === 'custom'
        ? 'カスタムAIプロバイダーを有効にしました — 全AI機能がローカルLLMを使用します'
        : 'Emergent（既定）に戻しました');
    };
    try {
      await put(cfg.version);
    } catch (e) {
      // 409 = the stored version moved on; retry once with the reported version.
      if (e?.response?.status === 409) {
        try {
          await put(e.response.data?.detail_obj?.current_version ?? null);
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || '保存に失敗しました');
        }
      } else {
        toast.error(e?.response?.data?.detail || '保存に失敗しました');
      }
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/provider/test`,
        { base_url: cfg.base_url, api_key: apiKey, model: cfg.text_model,
          cf_access_client_id: cfg.cf_access_client_id || '', cf_access_client_secret: cfSecret },
        { withCredentials: true });
      setTestResult(r.data);
      if (r.data.ok) toast.success('接続に成功しました');
      else toast.error('接続に失敗しました');
    } catch (e) {
      const msg = e?.response?.data?.detail || '接続テストに失敗しました';
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm" data-testid="ai-provider-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 px-6 py-4">
        <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
          <HardDrives size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-zinc-900">AIプロバイダー（管理者）</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            全AIチャットボットとOCRをローカルLLM（OpenAI互換API: Ollama / LM Studio / vLLM 等）へ切り替えられます。いつでもEmergentへ戻せます。
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${
          isCustom ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}
          data-testid="ai-provider-mode-badge">
          {isCustom ? 'カスタム（ローカルLLM）使用中' : 'Emergent（既定）使用中'}
        </span>
      </div>

      <div className="space-y-4 px-6 py-5">
        <div className="flex rounded-lg border border-zinc-200 overflow-hidden w-fit" data-testid="ai-provider-mode-toggle">
          {[['emergent', 'Emergent（既定）'], ['custom', 'カスタム（ローカルLLM）']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => set('mode', v)}
              data-testid={`ai-provider-mode-${v}`}
              className={`px-3.5 py-2 text-xs font-semibold transition-colors ${
                cfg.mode === v ? 'bg-indigo-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {cfg.mode === 'custom' && (
          <div className="space-y-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
            <Field label="エンドポイントURL"
              hint="OpenAI互換サーバーのベースURL（例: http://192.168.1.50:11434）。このサーバーから到達可能である必要があります — クラウド本番環境からNAS/LAN内のIPには直接接続できないため、トンネル等での公開が必要です。">
              <Input value={cfg.base_url} onChange={(e) => set('base_url', e.target.value)}
                placeholder="http://192.168.1.50:11434" className="h-9 text-sm font-mono"
                data-testid="ai-provider-url" />
            </Field>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold text-zinc-600">使用モデル</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-400" data-testid="ai-provider-models-count">
                    {online.length > 0 ? `${online.length} 件検出済み` : '未検出'}
                  </span>
                  <Button size="sm" variant="outline" onClick={detectModels} disabled={detecting || !cfg.base_url}
                    className="h-7 text-[11px]" data-testid="ai-provider-detect-models">
                    <ArrowsClockwise size={12} className={`mr-1 ${detecting ? 'animate-spin' : ''}`} />
                    {detecting ? '検出中…' : 'モデルを検出'}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="テキストモデル" hint="チャット・アシスタント・ダイジェスト等に使用">
                  <ModelSelect value={cfg.text_model} onChange={(v) => set('text_model', v)}
                    options={textOptions} placeholder="llama3.1:8b" testid="ai-provider-text-model" />
                </Field>
                <Field label="画像モデル（Vision）" hint="OCR・画像処理に使用">
                  <ModelSelect value={cfg.vision_model} onChange={(v) => set('vision_model', v)}
                    options={visionOptions} placeholder="llava:13b" testid="ai-provider-vision-model" />
                </Field>
                <Field label="埋め込みモデル（意味検索）"
                  hint="書類の意味検索（AI検索）に使用。未設定のあいだ意味検索は「未設定」と表示されます。この端末にモデルは入れません。">
                  <ModelSelect value={cfg.embedding_model} onChange={(v) => set('embedding_model', v)}
                    options={embeddingOptions} placeholder="bge-m3" testid="ai-provider-embedding-model" />
                </Field>
              </div>
              {online.length === 0 && (
                <p className="rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] text-zinc-500" data-testid="ai-provider-models-hint">
                  「モデルを検出」を押すとサーバー上のモデル一覧から選択できます（未検出の間は手入力）
                </p>
              )}
            </div>
            <Field label="APIキー（任意）"
              hint={cfg.has_api_key && apiKey === null ? '設定済みのキーがあります（変更しない場合は空のまま）' : '認証不要のサーバーでは空欄のままにしてください'}>
              <Input type="password" value={apiKey ?? ''} onChange={(e) => setApiKey(e.target.value)}
                placeholder={cfg.has_api_key ? '●●●●●●（設定済み）' : 'sk-...'}
                className="h-9 text-sm" data-testid="ai-provider-api-key" />
            </Field>
            <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-indigo-600" />
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">OCR・画像処理もローカルLLMを使う</p>
                  <p className="text-[11px] text-zinc-400">
                    OFFの場合、書類OCR等の画像処理のみEmergent（Gemini Vision）を使います
                    — その場合は下の「Emergentへの呼び出しを許可」もONが必要です
                  </p>
                </div>
              </div>
              <Switch checked={cfg.use_for_vision} onCheckedChange={(v) => set('use_for_vision', v)}
                data-testid="ai-provider-use-vision" />
            </div>
            <div className={`flex items-center justify-between rounded-lg border px-3 py-2.5 ${
              cfg.allow_emergent_calls ? 'border-amber-300 bg-amber-50' : 'border-teal-200 bg-teal-50/60'}`}>
              <div className="flex items-center gap-2">
                <CloudSlash size={16} className={cfg.allow_emergent_calls ? 'text-amber-600' : 'text-teal-700'} />
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">Emergentへの呼び出しを許可</p>
                  <p className="text-[11px] text-zinc-500">
                    {cfg.allow_emergent_calls
                      ? '許可中 — 機能ルーティングで emergent:// を指定した機能はEmergentを呼び、クレジットを消費します'
                      : 'OFF（推奨）— ローカルLLMのみを使い、Emergentへは1回も呼び出しません。残っているemergent://の指定は自動で社内モデルに読み替えます'}
                  </p>
                </div>
              </div>
              <Switch checked={!!cfg.allow_emergent_calls}
                onCheckedChange={(v) => set('allow_emergent_calls', v)}
                data-testid="ai-provider-allow-emergent" />
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3">
              <p className="text-[12px] font-semibold text-amber-800">Cloudflare Access サービストークン（任意・推奨）</p>
              <p className="text-[11px] text-amber-700/80">
                トンネル（例: llm.sensbey.link）を Cloudflare Access で保護している場合に入力します。Zero Trust → Access → Service Auth → Service Tokens で発行できます。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Client ID">
                  <Input value={cfg.cf_access_client_id || ''} onChange={(e) => set('cf_access_client_id', e.target.value)}
                    placeholder="xxxx.access" className="h-9 text-sm font-mono bg-white" data-testid="ai-provider-cf-id" />
                </Field>
                <Field label="Client Secret"
                  hint={cfg.has_cf_secret && cfSecret === null ? '設定済み（変更しない場合は空のまま）' : ''}>
                  <Input type="password" value={cfSecret ?? ''} onChange={(e) => setCfSecret(e.target.value)}
                    placeholder={cfg.has_cf_secret ? '●●●●●●（設定済み）' : ''}
                    className="h-9 text-sm bg-white" data-testid="ai-provider-cf-secret" />
                </Field>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={test} disabled={testing || !cfg.base_url}
                className="h-8 text-xs" data-testid="ai-provider-test-btn">
                <PlugsConnected size={14} className="mr-1" /> {testing ? '接続テスト中…' : '接続テスト'}
              </Button>
              {testResult && (
                <span className={`text-xs font-semibold ${testResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}
                  data-testid="ai-provider-test-result">
                  {testResult.ok ? `✓ 接続成功（応答: ${testResult.reply || 'OK'}）` : `✗ ${testResult.error}`}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-4">
          {isCustom && (
            <Button size="sm" variant="outline" disabled={saving}
              onClick={() => save({ mode: 'emergent' })}
              className="h-9 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid="ai-provider-revert-btn">
              <ArrowCounterClockwise size={14} className="mr-1" /> Emergentに戻す
            </Button>
          )}
          <Button size="sm" onClick={() => save()} disabled={saving}
            className="h-9 text-xs bg-teal-700 hover:bg-teal-800" data-testid="ai-provider-save-btn">
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>
    </div>
  );
}
